import { Injectable } from '@angular/core';
import { GoogleArchiveService } from '../../../services/google-archive.service';
import { ToastService } from '../../../services/toast.service';
import { NarrativeScene, StyleConfiguration, GeneratedImage } from '../models/narrative.model';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, toArray } from 'rxjs/operators';

export interface NarrativeImageSaveResult {
  success: boolean;
  savedCount: number;
  errors: string[];
}

@Injectable({
  providedIn: 'root'
})
export class NarrativeGoogleService {
  constructor(
    private googleArchiveService: GoogleArchiveService,
    private toastService: ToastService
  ) { }

  /**
   * 選択された画像をGoogle Drive/Sheetsに保存
   */
  saveSelectedImages(
    scenes: NarrativeScene[],
    styleConfig: StyleConfiguration
  ): Observable<NarrativeImageSaveResult> {
    const selectedScenes = this.getSelectedScenes(scenes);

    if (selectedScenes.length === 0) {
      this.toastService.showWarning('保存する画像が選択されていません');
      return of({ success: false, savedCount: 0, errors: ['No images selected'] });
    }

    // 各シーンの画像を順番に保存（concatMapで順次処理）
    return from(selectedScenes).pipe(
      concatMap((scene, index) => this.saveSceneImage(scene, styleConfig, index + 1, selectedScenes.length).pipe(
        catchError(error => {
          console.error('Failed to save scene image:', error);
          return of({ success: false, error: error.message });
        })
      )),
      toArray(),
      concatMap(results => {
        const savedCount = results.filter(r => r.success).length;
        const errors = results
          .filter(r => !r.success)
          .map(r => r.error || 'Unknown error');

        return of({
          success: savedCount > 0,
          savedCount,
          errors
        });
      })
    );
  }

  /**
   * 選択されている画像があるシーンを取得
   */
  private getSelectedScenes(scenes: NarrativeScene[]): NarrativeScene[] {
    return scenes.filter(scene =>
      scene.images &&
      scene.images.length > 0 &&
      scene.selectedImageIndex !== undefined &&
      scene.selectedImageIndex >= 0
    );
  }

  /**
   * 単一のシーン画像を保存
   */
  private saveSceneImage(
    scene: NarrativeScene,
    styleConfig: StyleConfiguration,
    sceneNumber?: number,
    totalScenes?: number
  ): Observable<any> {
    const selectedImage = scene.images[scene.selectedImageIndex!];

    // シーン番号を含めたプロンプトを作成
    let promptWithSceneInfo = scene.prompt;
    if (sceneNumber && totalScenes) {
      promptWithSceneInfo = scene.prompt;
    }

    // Firefly APIのパラメータ形式に変換
    const parameters = this.buildFireflyParameters(scene, styleConfig);

    return this.googleArchiveService.saveFireflyImage(
      selectedImage.url,
      promptWithSceneInfo,
      parameters,
      scene.seed
    );
  }

  /**
   * Firefly API互換のパラメータを構築
   */
  private buildFireflyParameters(
    scene: NarrativeScene,
    styleConfig: StyleConfiguration
  ): any {
    // カスタムモデル使用時はstyleConfigのmodelVersionを使用、そうでなければデフォルト
    const modelVersion = styleConfig.type === 'custom' && styleConfig.modelVersion
      ? styleConfig.modelVersion
      : 'image4_standard';

    const params: any = {
      contentClass: '',
      available: "OK",
      modelVersion: modelVersion,
      promptBiasingLocaleCode: 'ja-JP',
      width: 2688,  // NARRATIVE_CONSTANTS.IMAGE_GENERATION.DEFAULT_SIZE.width
      height: 1536, // NARRATIVE_CONSTANTS.IMAGE_GENERATION.DEFAULT_SIZE.height
      visualIntensity: styleConfig.visualIntensity || 2,
      seed: scene.seed || 0
    };

    // ネガティブプロンプトがある場合
    if (scene.negativePrompt) {
      params.negativePrompt = scene.negativePrompt;
    }

    // スタイル画像使用時
    if (styleConfig.type === 'image' && styleConfig.imageFileId) {
      params.styleStrength = styleConfig.strength || 100;
      params.styleImageId = styleConfig.imageFileId;
    }

    // カスタムモデル使用時
    if (styleConfig.type === 'custom' && styleConfig.customModelId) {
      params.customModelName = styleConfig.customModelId;
    }

    return params;
  }
}